import { SearchResultItem } from "@/hooks/useGlobalSearch";
import { cn } from "@/utils";
import { formatDistanceToNow } from "date-fns";
import { Inbox, Plug, ScrollText } from "lucide-react";
import React from "react";

interface GlobalSearchResultItemProps {
  item: SearchResultItem;
  onSelect: () => void;
}

const getCategoryIcon = (itemType: SearchResultItem["itemType"]) => {
  switch (itemType) {
    case "skills":
      return ScrollText;
    case "connections":
      return Plug;
    case "inbox":
      return Inbox;
  }
};

const getCategoryLabel = (itemType: SearchResultItem["itemType"]) => {
  switch (itemType) {
    case "skills":
      return "Skill";
    case "connections":
      return "Connector";
    case "inbox":
      return "Inbox";
  }
};

const getCategoryColor = (itemType: SearchResultItem["itemType"]) => {
  switch (itemType) {
    case "skills":
      return "text-plum";
    case "connections":
      return "text-spectrum-mulberry";
    case "inbox":
      return "text-cyan-500";
  }
};

export const GlobalSearchResultItem: React.FC<GlobalSearchResultItemProps> = ({
  item,
  onSelect,
}) => {
  const Icon = getCategoryIcon(item.itemType);
  const label = getCategoryLabel(item.itemType);
  const color = getCategoryColor(item.itemType);
  const updatedAt = item.data.updated_at
    ? new Date(item.data.updated_at)
    : null;

  const truncatedDescription =
    item.description && item.description.length > 200
      ? `${item.description.slice(0, 200)}...`
      : item.description;

  return (
    <div
      className="flex items-start gap-3 p-2 cursor-pointer"
      onClick={onSelect}
    >
      <div className={cn("flex-shrink-0 mt-0.5", color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{item.name}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {label}
          </span>
          {updatedAt && (
            <>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                •
              </span>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                Updated {formatDistanceToNow(new Date(updatedAt))} ago
              </span>
            </>
          )}
        </div>
        {truncatedDescription && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {truncatedDescription}
          </p>
        )}
      </div>
    </div>
  );
};
